import {Loading} from 'components/Loading';
import {inputMenuActionType, FormSelectionEnum, methodType, SWRKeyEnum} from 'interfaces';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Switch,
  Typography,
  FormControlLabel,
  Stack,
  TableContainer,
  Divider,
} from '@mui/material';
import {useInputMenuContext} from 'context/InputMenu';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import {PlansTable} from 'components/PlansTable';
import GenericPaper from 'components/Generic/Paper';
import GenericPaperTitle from 'components/Generic/PaperTitle';
import {TagSelector} from 'components/PlanList/TagSelector';
import {SearchPlan} from 'components/PlanList/SearchPlan';
import {AddFolderDialog} from 'components/PlanList/AddFolderDialog';
import Alert from 'helpers/Alert';
import {EMPTY_ID, useGetFolders} from 'hooks/swr';
import {useAuth0} from '@auth0/auth0-react';
import AddIcon from '@mui/icons-material/Add';
import {useTranslation} from 'react-i18next';
import type {Plan, Folder} from 'client';
import {useState} from 'react';
import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';
import {MyPlansSwitch, useGetUserPlans} from '../../../components/Shared/UserPlans';

const PlanListView = () => {
  const {t} = useTranslation('common');
  const dataFolders = useGetFolders({resourceId: EMPTY_ID});
  const {user} = useAuth0();
  const userFolders = user && dataFolders.data;
  const {plans, userPlans} = useGetUserPlans();

  const [selectedPlanIds, setSelectedPlanIds] = useState<string[] | undefined>();
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[] | undefined>();
  const [showFolders, setShowFolders] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const handleDialogOpen = () => {
    setOpen(true);
  };
  const handleDialogClose = () => {
    setOpen(false);
  };

  const createFolder = (name: string) => {
    backendCommunicator
      .createFolder(name)
      .then(() => {
        void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
      })
      .catch((error) => {
        Alert.error(error);
      });
  };

  const selectPlanIds = (plans: Plan[]) => {
    setSelectedPlanIds(plans.map(({id}) => id));
  };

  const selectFolderIds = (folders: Folder[]) => {
    setSelectedFolderIds(folders.map(({id}) => id));
  };

  const setPlansHandler = (plans: Plan[]) => {
    selectPlanIds(plans);
    void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
  };

  if (!plans) {
    return <Loading />;
  }

  if (!userFolders) {
    return <Loading />;
  }

  return (
    <Box p={1}>
      {plans.length === 0 ? (
        <OnboardingCard />
      ) : (
        <GenericPaper>
          <Box sx={{display: 'flex'}} justifyContent="space-between" alignItems="center">
            <GenericPaperTitle title={t('planList')} p={2} />

            <Stack spacing={1} direction={'row'} alignItems={'center'} pr={2}>
              <SearchPlan
                allPlans={plans}
                setSelectedPlans={(plans: Plan[]) => {
                  setPlansHandler(plans);
                }}
                allFolders={userFolders}
                setSelectedFolders={(folders: Folder[]) => {
                  selectFolderIds(folders);
                  void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
                }}
              />
              <TagSelector allPlans={plans} setSelectedPlans={selectPlanIds} />

              <FormControlLabel
                label={
                  <Typography variant="body2" color="secondary" sx={{userSelect: 'none'}}>
                    {showFolders ? t('showFolders') : t('hideFolders')}
                  </Typography>
                }
                control={
                  <Switch
                    sx={{m: 1}}
                    size="small"
                    checked={showFolders}
                    onChange={() => {
                      setShowFolders(!showFolders);
                    }}
                  />
                }
              />
              <MyPlansSwitch userPlans={userPlans} />
            </Stack>
          </Box>
          <Box>
            <TableContainer sx={{maxHeight: 'calc(100vh - 194px)'}}>
              {selectedPlanIds ? (
                <PlansTable
                  plans={plans.filter(({id}) => selectedPlanIds.includes(id))}
                  folders={
                    selectedFolderIds ? userFolders.filter(({id}) => selectedFolderIds.includes(id)) : userFolders
                  }
                  showFolders={showFolders}
                />
              ) : (
                <PlansTable plans={plans} folders={userFolders} showFolders={showFolders} />
              )}
            </TableContainer>
            <Divider />
            <Stack direction="row" spacing={2} p={2} justifyContent="flex-end">
              <AddPlan />
              <AddFolder handleDialogOpen={handleDialogOpen} />
            </Stack>
          </Box>
          <AddFolderDialog
            open={open}
            handleCreate={(name) => {
              createFolder(name);
            }}
            handleClose={handleDialogClose}
          />
        </GenericPaper>
      )}
    </Box>
  );
};

export default PlanListView;

const OnboardingCard = () => {
  const {t} = useTranslation('common');

  const inputContext = useInputMenuContext();

  const showCreatePlanForm = () => {
    const inputMenuDispatch = inputContext?.dispatch;

    inputMenuDispatch &&
      inputMenuDispatch({
        type: inputMenuActionType.openInputMenu,
        method: methodType.add,
        form: FormSelectionEnum.plan,
      });
  };

  return (
    <Card variant="outlined">
      <CardHeader title={t('welcomeDialogTitle')} />
      <CardContent>
        <Box px={4}>
          <Typography component="p">{t('welcomeDialogContent')}</Typography>
          <Box mx={10} my={5}>
            <Button
              type="submit"
              size="large"
              fullWidth
              disableElevation
              variant="contained"
              color="primary"
              onClick={showCreatePlanForm}
              data-testid="plan-add-button"
            >
              {t('welcomeDialogCTAButtonText')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AddPlan = () => {
  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const {t} = useTranslation('common');

  return (
    <Button
      variant="contained"
      size="small"
      color="primary"
      style={{textTransform: 'capitalize'}}
      onClick={() => {
        inputMenuDispatch &&
          inputMenuDispatch({
            type: inputMenuActionType.openInputMenu,
            form: FormSelectionEnum.plan,
            method: methodType.add,
          });
      }}
      endIcon={<AddIcon />}
      data-testid="plan-add-button"
    >
      {t('addPlan')}
    </Button>
  );
};

const AddFolder = ({handleDialogOpen}) => {
  const {t} = useTranslation('common');

  return (
    <Button
      variant="contained"
      size="small"
      color="primary"
      style={{textTransform: 'capitalize'}}
      onClick={handleDialogOpen}
      endIcon={<CreateNewFolderIcon />}
      data-testid="folder-add-button"
    >
      {t('addFolder')}
    </Button>
  );
};
